/**
 * Spec → FlowDocument 어댑터.
 *
 * 이사품목 Spec(서버 도메인 모델)을 item-picker sub-flow로 변환한다.
 * - AttrDef[] + 마지막 serviceSet 질문을 **하나의 step** 안의 questions 배열로 묶어
 *   L2 layout 의 push-up cascade(이전 Q/A 누적 표시)가 모든 짐 입력 화면에서
 *   동작하도록 한다. 각 attribute 를 별도 step 으로 분리하면 step 마다 Inquiry
 *   인스턴스가 새로 생성되어 history 가 비고, cascade 가 첫 질문에서만 보이는
 *   회귀가 발생한다.
 * - AttrDef.conditions 의 GUIDE_MESSAGE 효과는 question.guideMessage(콜아웃) 로 인코딩
 *   → 사용자가 답변을 채워가며 즉시 가이드 문구가 바뀐다 (백엔드 compile 호출 없이)
 * - SERVICE_TYPE_CONSTRAINT 의 동적 처리는 호출 측에서 useCompileMovingItem 결과의
 *   `allowedServiceSets` 를 이 함수 인자로 넘겨주는 방식으로 처리한다.
 *   (어댑터는 정적 옵션 매핑만 담당 — Spec 단일 평가 시점에는 attr 값이 없어 안전한
 *   동적 매핑이 어렵기 때문)
 * - 마지막 question 에서 서비스 범위(serviceSet) 선택 → terminal: 'instance-done'
 *
 * design-system의 FlowDocument는 도메인을 알지 못한다 — 모든 도메인 지식은
 * 이 어댑터에서 매핑된다.
 */

import type { FlowDocument, Expr } from '@miso/design-system';

type QuestionsStep = Extract<FlowDocument['steps'][string], { kind: 'questions' }>;
type QuestionDoc = QuestionsStep['questions'][number];
type GuideMessageDoc = NonNullable<QuestionDoc['guideMessage']>;
import type {
  AttrDef,
  AttributeClause,
  AttributeCondition,
  ConditionRule,
  Spec,
} from './itemSpec';
import {
  SERVICE_SET_VALUES,
  SERVICE_SET_LABELS,
  SERVICE_SET_REQUIRED_TYPE_KEYS,
  isServiceSet,
  serviceTypeKeysToServiceSets,
  type ServiceSet,
} from './serviceSets';

// ── Layout / CTA policy ─────────────────────────────────────────────────────
//
// 짐 기록 플로우는 모든 질문에서 L2 layout(이전 Q/A를 push-up cascade로 누적)을
// 사용한다. attribute 입력과 serviceSet 선택을 단일 step 의 questions 배열로
// 묶어, 같은 Inquiry 인스턴스가 history 를 누적하도록 한다.
// (영상 녹화 step만 video-full layout을 별도 사용 — root flow 측에서 지정.)
const ITEM_FLOW_LAYOUT = 'L2' as const;

/** 마지막이 아닌 모든 question 의 CTA 라벨 (사용자 액션 = "다음으로 진행"). */
const NEXT_LABEL = '다음';
/** 마지막 question(서비스 범위 선택)에서 인스턴스 작성 완료 시 사용. */
const COMPLETE_LABEL = '완료';

// ── Helpers ───────────────────────────────────────────────────────────────────

function attrToQuestionId(key: string): string {
  return `attr__${key}`;
}

/** 짐 spec sub-flow 의 단일 step ID. */
const SUB_FLOW_STEP_ID = 'fill';
/** 서비스 범위 선택 question 의 ID (step ID 가 아니라 question ID 임에 유의). */
const SERVICE_SET_QUESTION_ID = 'serviceSet';
const TERMINAL_DONE = 'instance-done';

// ── Attribute suffix(unit) 추출 ───────────────────────────────────────────────
//
// `AttrDef` 도메인 타입에는 별도 `unit` 필드가 없다. 대신 spec 의
// `displayNameTemplate` 과 `displayTemplateRules[].template` 의 placeholder 형식
// `{attr:KEY:SUFFIX}` 가 단위(인치/kg 등) 정보를 담는 **유일한 표준 채널**이다.
// 예: `"{displayName} {attr:inch:인치}"` → key=inch, suffix='인치'.
//
// 본 어댑터는 spec 의 모든 template 문자열을 스캔해 `key → suffix` 매핑을
// 만들고, NUMBER attribute 의 question.input.suffix 로 사용한다.
// 매칭이 없으면 빈 문자열 — runtime 의 stepper 렌더러가 라벨 없이 자연스럽게
// "숫자 + 스테퍼" 만 표시한다.

const ATTR_PLACEHOLDER_RE = /\{attr:([^:}]+):([^}]+)\}/g;

/**
 * 단일 template 문자열에서 `{attr:KEY:SUFFIX}` 패턴을 모두 뽑아 누적 map 에 채운다.
 * 같은 key 에 다른 suffix 가 등장하면 첫 매치를 우선한다 (덮어쓰지 않음).
 */
function collectAttrSuffixesFromTemplate(
  template: string | null | undefined,
  out: Record<string, string>
): void {
  if (!template) return;
  for (const m of template.matchAll(ATTR_PLACEHOLDER_RE)) {
    const key = m[1];
    const suffix = m[2];
    if (!key || !suffix) continue;
    if (!(key in out)) out[key] = suffix;
  }
}

/**
 * Spec 의 모든 template 문자열을 합쳐 `attr.key → suffix` 맵을 만든다.
 *
 * 우선순위:
 * 1. `displayNameTemplate` (기본 표시명)
 * 2. `displayTemplateRules[].template` (조건부 표시명) — 위 1 에 매치 없을 때만 사용
 *
 * 같은 key 가 여러 위치에서 다른 suffix 로 등장해도 가장 먼저 만난 값이 유지된다.
 */
export function extractAttrSuffixes(spec: Spec): Record<string, string> {
  const out: Record<string, string> = {};
  collectAttrSuffixesFromTemplate(spec.displayNameTemplate, out);
  for (const rule of spec.displayTemplateRules ?? []) {
    collectAttrSuffixesFromTemplate(rule.template, out);
  }
  return out;
}

// ── ConditionRule → Expr ──────────────────────────────────────────────────────

/**
 * 단일 attribute key 컨텍스트에서 ConditionRule 을 InquiryFlow Expr 로 변환.
 *
 * 호출 측 attribute 의 question id 는 `attr__${key}` 이므로 var 경로도 동일하게 매핑한다.
 * NUMBER 비교는 답변이 문자열로 저장될 수 있으므로 `toInt` 로 강제 변환한다.
 * BOOLEAN 비교는 답변이 boolean 또는 'true'/'false' 문자열일 수 있어 양쪽 모두 OR 로 묶는다.
 */
export function ruleToExpr(rule: ConditionRule, attrKey: string): Expr {
  const attrVar = (k: string) => ({ var: attrToQuestionId(k) });

  switch (rule.type) {
    case 'ALWAYS_MATCH':
      return true;
    case 'NEVER_MATCH':
      return false;
    case 'NUMBER_GTE':
      return { '>=': [{ toInt: attrVar(attrKey) }, rule.threshold ?? 0] };
    case 'NUMBER_GT':
      return { '>': [{ toInt: attrVar(attrKey) }, rule.threshold ?? 0] };
    case 'NUMBER_LTE':
      return { '<=': [{ toInt: attrVar(attrKey) }, rule.threshold ?? 0] };
    case 'NUMBER_LT':
      return { '<': [{ toInt: attrVar(attrKey) }, rule.threshold ?? 0] };
    case 'BOOLEAN_EQ':
      return {
        or: [
          { '==': [attrVar(attrKey), rule.value ?? false] },
          { '==': [attrVar(attrKey), String(rule.value ?? false)] },
        ],
      };
    case 'ENUM_IN':
      return { in: [attrVar(attrKey), rule.values ?? []] };
    case 'AND':
      return { and: (rule.rules ?? []).map((r) => ruleToExpr(r, attrKey)) };
    case 'CROSS_AND':
      return {
        and: (rule.clauses ?? []).map((c: AttributeClause) => ruleToExpr(c.rule, c.key)),
      };
    default:
      // 미지원 RuleType 은 안전하게 false 처리.
      return false;
  }
}

/**
 * 스펙의 모든 조건을 소유 attribute key 와 함께 평탄화한다.
 *
 * 스펙 공통 조건(`spec.conditions`)은 소유 attribute 가 없어 key 가 빈 문자열이다.
 * 이 조건들은 AlwaysMatch 이거나 참조 key 를 스스로 갖는 CROSS_AND 뿐이므로
 * [ruleToExpr] 에 빈 key 를 넘겨도 안전하다.
 */
function ownedConditions(spec: Spec): { attrKey: string; condition: AttributeCondition }[] {
  const out: { attrKey: string; condition: AttributeCondition }[] = [];
  for (const condition of spec.conditions ?? []) {
    out.push({ attrKey: '', condition });
  }
  for (const attr of spec.attributes ?? []) {
    for (const condition of attr.conditions ?? []) {
      out.push({ attrKey: attr.key, condition });
    }
  }
  return out;
}

// ── conditions → conditional guideMessage ────────────────────────────────────

/**
 * 한 attribute 의 conditions 중 GUIDE_MESSAGE 효과만 모아 question.guideMessage 로 변환.
 *
 * 평가 시 위에서부터 첫 번째로 매치되는 GUIDE_MESSAGE 만 표시되며, 매치 없으면 미표시.
 * 같은 message 가 중복되면 한 번만 노출되도록 dedupe 한다.
 *
 * **변경 이력**: 이전에는 `description`(타이틀 아래 회색 보조 텍스트) 으로 매핑했지만,
 * Figma "상담 가이드" 디자인(input 위에 채색 콜아웃) 이 도입되면서
 * `guideMessage` 필드(콜아웃 박스) 로 변경. 한 question 에 description 과
 * guideMessage 가 동시에 필요한 케이스가 있다면 별도 매핑 채널을 추가해야 한다.
 */
export function buildGuideMessage(attr: AttrDef): GuideMessageDoc | undefined {
  const guideConditions = (attr.conditions ?? []).filter(
    (c): c is AttributeCondition & { effect: { type: 'GUIDE_MESSAGE'; message: string } } =>
      c.effect.type === 'GUIDE_MESSAGE' && typeof c.effect.message === 'string'
  );
  if (guideConditions.length === 0) return undefined;

  const seen = new Set<string>();
  const branches: { if: Expr; then: string }[] = [];
  for (const c of guideConditions) {
    const msg = c.effect.message;
    if (seen.has(msg)) continue;
    seen.add(msg);
    branches.push({ if: ruleToExpr(c.rule, attr.key), then: msg });
  }

  // text 필드는 description 과 동일한 표현력(static · template · conditional) 을
  // 가지므로 conditional 형태를 그대로 넣을 수 있다.
  return { text: { conditional: branches }, variant: 'info' };
}

// ── AttrDef → QuestionDoc ─────────────────────────────────────────────────────
//
// 본 함수는 layout 과 ctaLabel 을 주입하지 않는다. specToFlowDocument 가 모든
// question 을 모은 뒤 위치(마지막 여부)를 알 때 일괄 주입한다.

function attrDefToQuestion(attr: AttrDef, attrSuffixes: Record<string, string> = {}): QuestionDoc {
  const id = attrToQuestionId(attr.key);
  const title = attr.name ?? attr.key;
  const guideMessage = buildGuideMessage(attr);
  const guidePart = guideMessage ? { guideMessage } : {};

  switch (attr.type) {
    case 'ENUM': {
      const opts = (attr.values ?? []).map((v) => ({
        value: v,
        label: attr.enumDisplayNames?.[v] ?? v,
      }));
      return {
        id,
        title,
        ...guidePart,
        input: { kind: 'single-select' as const, required: true, options: opts },
      };
    }
    case 'NUMBER': {
      // 단위(suffix) 는 displayNameTemplate / displayTemplateRules 의
      // `{attr:key:suffix}` placeholder 에서만 추출된다. 매칭이 없으면 빈 문자열.
      // 빈 문자열 → 런타임 stepper 가 라벨 없이 "숫자 + 스테퍼" 만 노출한다.
      const suffix = attrSuffixes[attr.key] ?? '';
      return {
        id,
        title,
        ...guidePart,
        input: {
          kind: 'suffix' as const,
          suffix,
          required: true,
          inputType: 'number' as const,
          placeholder: '0',
        },
      };
    }
    case 'BOOLEAN': {
      return {
        id,
        title,
        ...guidePart,
        input: { kind: 'binary' as const, required: true },
      };
    }
    case 'STRING':
    default: {
      return {
        id,
        title,
        ...guidePart,
        input: { kind: 'text' as const, required: false },
      };
    }
  }
}

// ── ServiceSet ↔ ServiceType 매핑 ────────────────────────────────────────────
//
// 매핑표와 양방향 환산은 `./serviceSets` 가 단일 출처다 (어드민 스펙 편집기도 같은
// 표를 쓴다). 기존 import 경로 호환을 위해 여기서 다시 내보낸다.

export { serviceTypeKeysToServiceSets };

/**
 * ConditionEffect 에서 허용 ServiceSet 목록을 꺼낸다.
 *
 * - `allowedServiceSets` (이미 SERVICE_SET 이름, 테스트/레거시)
 * - `allowedServiceTypeKeys` (서버 원본 TYPE 키) → `serviceTypeKeysToServiceSets` 변환
 */
function resolveAllowedServiceSets(effect: {
  allowedServiceSets?: string[];
  allowedServiceTypeKeys?: string[];
}): ServiceSet[] | null {
  if (Array.isArray(effect.allowedServiceSets) && effect.allowedServiceSets.length > 0) {
    return effect.allowedServiceSets.filter(isServiceSet);
  }
  if (Array.isArray(effect.allowedServiceTypeKeys) && effect.allowedServiceTypeKeys.length > 0) {
    return serviceTypeKeysToServiceSets(effect.allowedServiceTypeKeys);
  }
  return null;
}

// ── 조건부 ServiceSet 질문 ID 접두사 ─────────────────────────────────────────

/**
 * 조건부 서비스 범위 질문 ID 접두사.
 * `serviceSet_c_0`, `serviceSet_c_1`, … 형태로 생성된다.
 */
export const SERVICE_SET_COND_PREFIX = 'serviceSet_c_';

// ── ServiceSet question ──────────────────────────────────────────────────────

/**
 * 서비스 범위 질문을 생성한다.
 *
 * `SERVICE_TYPE_CONSTRAINT` 조건이 있는 spec 에서는 조건별 conditional 질문(skipIf 포함)을
 * 생성해 사용자가 속성을 선택하는 즉시 허용된 서비스 범위만 표시한다.
 *
 * - 조건 없음: `serviceSet` 단일 질문 (전체 서비스 범위)
 * - 조건 있음: 각 조건마다 `serviceSet_c_N` 질문 + 어떤 조건도 안 맞을 때 fallback `serviceSet`
 *
 * @returns QuestionDoc 배열 (조건 없으면 길이 1)
 */
function makeConditionalServiceSetQuestions(spec: Spec): QuestionDoc[] {
  // SERVICE_TYPE_CONSTRAINT conditions 수집
  type ConstraintEntry = {
    attrKey: string;
    rule: ConditionRule;
    allowedSets: ServiceSet[];
  };

  const constraints: ConstraintEntry[] = [];
  for (const { attrKey, condition } of ownedConditions(spec)) {
    if (condition.effect.type !== 'SERVICE_TYPE_CONSTRAINT') continue;
    const allowedSets = resolveAllowedServiceSets(condition.effect);
    if (allowedSets && allowedSets.length > 0) {
      constraints.push({ attrKey, rule: condition.rule, allowedSets });
    }
  }

  // 제약 없음 → 단일 전체 옵션 질문
  if (constraints.length === 0) {
    return [
      {
        id: SERVICE_SET_QUESTION_ID,
        title: '서비스 범위를 선택해주세요.',
        input: {
          kind: 'single-select' as const,
          required: true,
          options: SERVICE_SET_VALUES.map((s) => ({ value: s, label: SERVICE_SET_LABELS[s] })),
        },
      },
    ];
  }

  const questions: QuestionDoc[] = [];

  // 각 조건에 맞는 conditional 서비스 범위 질문
  for (let i = 0; i < constraints.length; i++) {
    const { attrKey, rule, allowedSets } = constraints[i];
    const condExpr = ruleToExpr(rule, attrKey);
    questions.push({
      id: `${SERVICE_SET_COND_PREFIX}${i}`,
      title: '서비스 범위를 선택해주세요.',
      skipIf: { not: condExpr },
      input: {
        kind: 'single-select' as const,
        required: true,
        options: allowedSets.map((s) => ({ value: s, label: SERVICE_SET_LABELS[s] })),
      },
    });
  }

  // fallback: 어떤 조건도 매칭 안 될 때 (속성 미입력 등)
  const allCondExprs = constraints.map(({ attrKey, rule }) => ruleToExpr(rule, attrKey));
  const skipFallbackExpr: Expr = allCondExprs.length === 1 ? allCondExprs[0] : { or: allCondExprs };

  questions.push({
    id: SERVICE_SET_QUESTION_ID,
    title: '서비스 범위를 선택해주세요.',
    skipIf: skipFallbackExpr,
    input: {
      kind: 'single-select' as const,
      required: true,
      options: SERVICE_SET_VALUES.map((s) => ({ value: s, label: SERVICE_SET_LABELS[s] })),
    },
  });

  return questions;
}

/**
 * 서비스 범위 질문 생성 (모드 프리셋 인지).
 *
 * - `presetServiceSet` 있음(상단 서비스 모드에서 진입) → **질문 생략([])**.
 *   상단 모드 탭 선택이 곧 서비스 선택이므로 sub-flow 에서 다시 묻지 않는다. serviceSet 은
 *   presetAnswers 로 이미 시드되어 자동 적용되고, 앱바에 "(분해/운반)"처럼 표기된다.
 *   (픽커에서 도달 불가한 모드는 이미 dim 처리되므로, 여기 도달한 preset 은 그 짐이
 *    지원하는 유효 서비스다. 속성값에 따라 갈리는 세부 케이스까지 sub-flow 에서 다시
 *    묻던 이전 동작은 "모드=서비스" 원칙에 어긋나 제거 — 에어컨 서비스 질문 잔존 이슈.)
 * - `presetServiceSet` 없음(레거시/직접 선택) → 기존 조건부 질문(skipIf) 그대로.
 */
function makeServiceSetQuestions(spec: Spec, presetServiceSet?: ServiceSet): QuestionDoc[] {
  if (presetServiceSet) return [];
  return makeConditionalServiceSetQuestions(spec);
}

// ── 서비스 범위 산출 ──────────────────────────────────────────────────────────

/**
 * Spec이 제공하는 서비스 옵션 중 가장 높은 단계를 결정할 때 사용하는 우선순위.
 *
 * 우선순위는 사용자 안내(item-picker description)에서 "이 짐은 어디까지 가능한가?"를
 * 한 줄로 표시하기 위한 것으로, 분해/운반/설치를 가장 풍부한 서비스로 본다.
 */
const SERVICE_SET_PRIORITY: ReadonlyArray<ServiceSet> = [
  'DISASSEMBLY_TRANSPORT_INSTALLATION',
  'DISASSEMBLY_TRANSPORT',
  'TRANSPORT_ONLY',
  'TRANSPORT_TO_FIRST_FLOOR',
  'DISPOSAL',
];

/**
 * Spec이 제공할 수 있는 가장 높은 서비스 범위를 산출한다(카드 "OO 가능" 라벨용).
 *
 * `pickerAllowedServiceSets`(도달 가능한 서비스 집합)와 **동일한 소스**를 사용해
 * 카드 라벨과 픽커 dim 이 항상 일치하도록 한다. 그 집합에서 우선순위가 가장 높은
 * 단계를 반환하며, 도달 가능한 서비스가 하나도 없으면 `null`.
 *
 * @returns 산출된 ServiceSet 또는 산출 불가 시 `null`.
 */
export function maxServiceSetForSpec(spec: Spec): ServiceSet | null {
  const allowed = new Set<ServiceSet>(pickerAllowedServiceSets(spec));
  for (const s of SERVICE_SET_PRIORITY) {
    if (allowed.has(s)) return s;
  }
  return null;
}

/**
 * item-picker 카드 description에 표시할 사용자 친화적 라벨.
 *
 * 형태: `"<서비스 라벨> 가능"` (예: "분해/운반/설치 가능", "운반만 가능").
 * 라벨은 도메인 표기인 `SERVICE_SET_LABELS` 를 그대로 사용한다.
 */
export function maxServiceSetLabel(spec: Spec): string | undefined {
  const max = maxServiceSetForSpec(spec);
  if (!max) return undefined;
  return `${SERVICE_SET_LABELS[max]} 가능`;
}

// ── 서비스 모드(item-picker 상단 탭) 도메인 헬퍼 ──────────────────────────────
//
// item-picker 상단의 "서비스 범위 모드"는 짐이 도달 가능한 서비스 범위를 기준으로
// 카드 dim(불가) 여부를 정한다. 이는 **카드에 표시되는 "OO 가능" 라벨과 반드시
// 일치해야** 한다 — 카드가 "분해/운반 가능"인데 분해/운반/설치 모드에서 dim 되지
// 않으면 사용자 혼선이 생긴다(에어컨 이슈).
//
// 그래서 `pickerAllowedServiceSets`(픽커 차단 기준)와 `maxServiceSetForSpec`
// (카드 라벨)은 **동일한 achievable-set** 을 쓴다:
//   - SERVICE_TYPE_CONSTRAINT 가 하나도 없으면 → 전체 허용(분해/운반/설치 포함).
//   - 제약이 있으면 → 모든 조건이 허용하는 서비스의 **합집합**(백엔드
//     ConditionEffectsAggregator 의 union 규칙과 동일). 합집합에 없는 모드는
//     이 짐이 절대 도달할 수 없으므로 dim.
//   - 속성값에 따라 갈리는 경우(가죽 vs 천)는 두 값 모두 조건으로 명시돼 union 에
//     들어가면 픽커에선 허용되고, 서브플로우에서 값별로 옵션 dim 된다.

/** 개별 서비스 TYPE 키의 한글 라벨 (토스트 사유 문구에 사용). */
const SERVICE_TYPE_LABELS: Record<string, string> = {
  DISASSEMBLY: '분해',
  TRANSPORT: '운반',
  INSTALLATION: '설치',
  TRANSPORT_TO_FIRST_FLOOR: '1층 안전운반',
  DISPOSAL: '버림',
};

/**
 * 픽커 게이팅용: spec 이 **무조건(ALWAYS_MATCH)** 지원 가능한 ServiceSet 집합.
 *
 * - 무조건 SERVICE_TYPE_CONSTRAINT 가 없으면 → 전체 허용(5종).
 * - 무조건 제약이 있으면 그 허용 집합들의 **교집합**을 취한다(모든 무조건 제약을 동시에 만족).
 * - 값-조건부(ENUM_IN 등) 제약은 무시한다 — 특정 속성값에선 해당 모드가 가능할 수 있으므로
 *   픽커에서 미리 막지 않고 서브플로우에서 판정한다.
 *
 * @returns 정규 순서(SERVICE_SET_VALUES)의 ServiceSet 배열.
 */
export function pickerAllowedServiceSets(spec: Spec): ServiceSet[] {
  const union = new Set<ServiceSet>();
  let hasConstraint = false;
  for (const { condition } of ownedConditions(spec)) {
    if (condition.effect.type !== 'SERVICE_TYPE_CONSTRAINT') continue;
    hasConstraint = true;
    const sets = resolveAllowedServiceSets(condition.effect);
    if (sets) for (const s of sets) union.add(s);
  }
  // 서비스 제약이 없으면 전체 허용(카드도 "분해/운반/설치 가능").
  if (!hasConstraint) return [...SERVICE_SET_VALUES];
  // 제약이 있으면 조건들이 허용하는 서비스의 합집합 = 이 짐이 도달 가능한 서비스.
  // (union 이 비면 [] — 아무 서비스도 불가하여 모든 모드에서 dim.)
  return SERVICE_SET_VALUES.filter((s) => union.has(s));
}

/** spec 이 무조건 제공 가능한 개별 서비스 TYPE 키 집합(pickerAllowedServiceSets 기반). */
function supportedServiceTypeKeys(spec: Spec): Set<string> {
  const keys = new Set<string>();
  for (const set of pickerAllowedServiceSets(spec)) {
    for (const key of SERVICE_SET_REQUIRED_TYPE_KEYS[set]) keys.add(key);
  }
  return keys;
}

/**
 * 모드가 요구하는 서비스 중 spec 이 **무조건적으로 제공 불가**한 항목의 한글 라벨.
 *
 * 예) 에어컨(무조건 분해/운반만) + 모드 분해/운반/설치 → 요구 {분해,운반,설치} − 지원 {분해,운반}
 *     = {설치} → ['설치'].  (토스트: "에어컨은 설치 불가 품목입니다")
 *
 * spec 이 해당 모드를 완전히 지원하면 빈 배열.
 */
export function missingServiceTypesForMode(spec: Spec, mode: ServiceSet): string[] {
  const required = SERVICE_SET_REQUIRED_TYPE_KEYS[mode];
  const supported = supportedServiceTypeKeys(spec);
  return required
    .filter((key) => !supported.has(key))
    .map((key) => SERVICE_TYPE_LABELS[key] ?? key);
}

/**
 * 모드-불가 토스트에 쓸 사유 문구(라벨 조합). 조사(은/는)는 호출 측에서 붙인다.
 * 예) '설치', '분해·설치'. (이론상) 사유 산출 불가 시 모드 라벨로 폴백.
 */
export function modeBlockReason(spec: Spec, mode: ServiceSet): string {
  const missing = missingServiceTypesForMode(spec, mode);
  return missing.length > 0 ? missing.join('·') : SERVICE_SET_LABELS[mode];
}

/** 서비스 모드 탭의 배치 그룹: 3단 계단(ladder) vs 독립 특수모드(special). */
export type ServiceModeGroup = 'ladder' | 'special';

export interface ServiceModeOption {
  /** 모드 식별자 = ServiceSet 값. */
  id: ServiceSet;
  label: string;
  group: ServiceModeGroup;
}

/**
 * item-picker 상단 모드 탭 구성(시안 A).
 * 계단 3단(운반만 ⊂ 분해/운반 ⊂ 분해/운반/설치) + 특수 2개(1층 안전운반·버림).
 * 계단은 왼→오 순서로 작업 범위가 커진다.
 */
export const SERVICE_MODE_OPTIONS: ServiceModeOption[] = [
  { id: 'TRANSPORT_ONLY', label: SERVICE_SET_LABELS.TRANSPORT_ONLY, group: 'ladder' },
  { id: 'DISASSEMBLY_TRANSPORT', label: SERVICE_SET_LABELS.DISASSEMBLY_TRANSPORT, group: 'ladder' },
  {
    id: 'DISASSEMBLY_TRANSPORT_INSTALLATION',
    label: SERVICE_SET_LABELS.DISASSEMBLY_TRANSPORT_INSTALLATION,
    group: 'ladder',
  },
  {
    id: 'TRANSPORT_TO_FIRST_FLOOR',
    label: SERVICE_SET_LABELS.TRANSPORT_TO_FIRST_FLOOR,
    group: 'special',
  },
  { id: 'DISPOSAL', label: SERVICE_SET_LABELS.DISPOSAL, group: 'special' },
];

/** 기본 선택 모드(항상 하나는 선택됨) — 분해/운반/설치. */
export const DEFAULT_SERVICE_MODE: ServiceSet = 'DISASSEMBLY_TRANSPORT_INSTALLATION';

// ── Main adapter ──────────────────────────────────────────────────────────────

/**
 * 하나의 Spec을 인스턴스 생성용 FlowDocument로 변환한다.
 *
 * 흐름:
 * 1. 모든 AttrDef + serviceSet 질문을 **단일 step**(`fill`) 의 questions 배열로
 *    묶어 L2 form(모든 질문을 한번에 표시) 이 적용되도록 한다.
 *    - AttrDef.conditions 의 GUIDE_MESSAGE 효과는 question.guideMessage 로 인코딩
 * 2. 모든 question 에 `layout: 'L2'` + `ctaLabel`(중간=다음, 마지막=완료)을 일괄 주입.
 * 3. terminal: instance-done (placeholder-auto kind — onComplete 자동 호출)
 *
 * @param spec - 이사품목 스펙
 * @param allowedServiceSets - 허용 서비스 범위 목록 (없으면 전체 허용).
 *   호출 측에서 useCompileMovingItem 결과의 `allowedServiceSets` 를 그대로 넘기면
 *   SERVICE_TYPE_CONSTRAINT 효과가 정적 옵션 매핑으로 반영된다.
 */
export function specToFlowDocument(
  spec: Spec,
  opts?: { presetServiceSet?: ServiceSet }
): FlowDocument {
  const attrSuffixes = extractAttrSuffixes(spec);
  const baseQuestions: QuestionDoc[] = [
    ...(spec.attributes ?? []).map((attr) => attrDefToQuestion(attr, attrSuffixes)),
    ...makeServiceSetQuestions(spec, opts?.presetServiceSet),
  ];

  // 참고: 속성이 없고 프리셋으로 서비스 질문까지 생략되면 questions 가 0개가 될 수 있다.
  // 이 경우 사용자에게 물을 것이 없으므로 sub-flow 를 즉시 완료해야 한다(서비스는 프리셋
  // 자동 적용). 빈 questions step 의 즉시 완료는 InquiryFlow(StepContent)가 처리하므로
  // 여기서 억지로 질문을 채우지 않는다. (억지로 채우면 유모차처럼 물을 필요 없는 짐에도
  // "서비스 범위" 질문이 다시 노출됨.)

  // layout / ctaLabel 일괄 주입.
  // - layout: 모든 question 에 L2 (cascade 누적)
  // - ctaLabel: 마지막 question 만 '완료', 그 외는 '다음'
  //   기본 라벨 정책(L1/L2Layout) 도 isLast 기준으로 자동 결정하지만, 의도를 명시해
  //   회귀 테스트에서 검증할 수 있도록 question 객체에 직접 넣는다.
  const questions: QuestionDoc[] = baseQuestions.map((q, idx) => {
    const isLast = idx === baseQuestions.length - 1;
    return {
      ...q,
      layout: ITEM_FLOW_LAYOUT,
      ctaLabel: isLast ? COMPLETE_LABEL : NEXT_LABEL,
    };
  });

  // Flow 전역 메타: AppBar 가운데 "짐이름 (서비스)", 우측 'i' 아이콘 → spec 설명.
  // - appBarTitle: 모드(프리셋)로 진입한 경우 적용될 서비스를 함께 표기해
  //   sub-flow 안에서도 "의자 (분해/운반)"처럼 어떤 서비스로 기록 중인지 보이게 한다.
  //   (프리셋 없음 = 서비스를 sub-flow 에서 고르는 레거시 흐름이면 짐 이름만 노출.)
  // - infoMessage: spec.description 이 의미 있을 때만 주입 (빈 문자열은 아이콘 자체를 숨김).
  const appBarTitle = opts?.presetServiceSet
    ? `${spec.name} (${SERVICE_SET_LABELS[opts.presetServiceSet]})`
    : spec.name;
  const meta: FlowDocument['meta'] = {
    appBarTitle,
    ...(typeof spec.description === 'string' && spec.description.trim().length > 0
      ? { infoMessage: spec.description }
      : {}),
  };

  return {
    schemaVersion: 1,
    id: `spec-flow-${spec.id}`,
    initial: SUB_FLOW_STEP_ID,
    meta,
    steps: {
      [SUB_FLOW_STEP_ID]: {
        kind: 'questions' as const,
        questions,
        next: { kind: 'terminal' as const, target: TERMINAL_DONE },
      },
    },
    terminals: {
      // `placeholder-auto`는 InquiryFlow의 built-in sentinel kind이다.
      // Terminal 화면을 별도 등록할 필요 없이 도달 즉시 onComplete가 호출된다.
      // (호출 측 `MovingItemsFlowAdapter`는 `registries={{ terminals: {} }}`로
      //  넘기므로 `instance-done` 같은 커스텀 kind는 staticValidator에서
      //  `UnknownRef` 에러를 throw하여 흰 화면을 유발했다.)
      [TERMINAL_DONE]: { kind: 'placeholder-auto' },
    },
  };
}

// ── ItemPickerInstance → MovingItemRequest ────────────────────────────────────

import type { ItemPickerInstance } from '@miso/design-system';

/** 서버로 보낼 짐 한 건. 앱의 `MovingItemRequest` 가 그대로 대입된다. */
export interface MovingItemRequest {
  specId: string;
  attributes: Record<string, string>;
  serviceSet: ServiceSet;
}

/**
 * ItemPickerInstance.answers를 MovingItemRequest로 변환한다.
 *
 * answers 구조:
 *   { [`attr__${key}`]: value, serviceSet: ServiceSet }
 */
export function instanceToMovingItemRequest(instance: ItemPickerInstance): MovingItemRequest {
  const attrs: Record<string, string> = {};

  for (const [key, value] of Object.entries(instance.answers)) {
    // serviceSet 관련 질문 ID 는 attribute 변환에서 제외
    if (key === SERVICE_SET_QUESTION_ID || key.startsWith(SERVICE_SET_COND_PREFIX)) continue;
    const attrKey = key.startsWith('attr__') ? key.slice(6) : key;
    if (value !== null && value !== undefined) {
      attrs[attrKey] = String(value);
    }
  }

  // 조건부 서비스 범위 질문 우선, 없으면 fallback `serviceSet` 사용
  const conditionalEntry = Object.entries(instance.answers).find(
    ([k, v]) => k.startsWith(SERVICE_SET_COND_PREFIX) && v != null && v !== ''
  );
  const serviceSet =
    ((conditionalEntry?.[1] ?? instance.answers[SERVICE_SET_QUESTION_ID]) as ServiceSet) ??
    'TRANSPORT_ONLY';

  return {
    specId: instance.itemId,
    attributes: attrs,
    serviceSet,
  };
}
